import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import OnboardingModal, { OnboardingData } from "./OnboardingModal";
import { db } from "../firebase";
import {
	doc,
	getDoc,
	setDoc,
	updateDoc,
	collection,
	query,
	where,
	getDocs,
	addDoc,
	serverTimestamp,
} from "firebase/firestore";

// Local flag to avoid showing again until next auth re-login (not route change)
const LATER_FLAG_KEY = "onboardingLaterUntilNextLogin";

const OnboardingGate: React.FC = () => {
	const { user } = useAuth();
	const [open, setOpen] = useState(false);
	const [checking, setChecking] = useState(true);

	useEffect(() => {
		let cancelled = false;
		const check = async () => {
			setChecking(true);
			try {
				if (!user) {
					setOpen(false);
					return;
				}
				// If user just logged in, session reset should clear this flag externally.
				// We do not persist the "later" across login sessions.
				const later = sessionStorage.getItem(LATER_FLAG_KEY);
				if (later === "1") {
					setOpen(false);
					return;
				}
				const ref = doc(db, "userProfiles", user.uid);
				const snap = await getDoc(ref);
				const data = snap.exists() ? (snap.data() as any) : {};
				const completed = !!data?.onboarding?.completed;
				if (!completed) {
					setOpen(true);
				} else {
					setOpen(false);
				}
			} finally {
				if (!cancelled) setChecking(false);
			}
		};
		check();
		return () => {
			cancelled = true;
		};
	}, [user]);

	const handleComplete = async (payload: OnboardingData) => {
		if (!user) return;
		const ref = doc(db, "userProfiles", user.uid);
		// Sanitize payload: Firestore doesn't allow undefined values
		const onboardingData: any = {
			completed: true,
			completedAt: new Date(),
			...payload,
		};
		Object.keys(onboardingData).forEach((k) => {
			if (onboardingData[k] === undefined) delete onboardingData[k];
		});
		await setDoc(
			ref,
			{
				onboarding: onboardingData,
				preferences: {
					// example place to store interests for personalization
					interests: payload.interests,
					selfAssessedLevel: payload.level,
					followedCategoryIds: payload.followCategories || [],
					notificationsCategoryPosts: !!payload.notifyCategoryPosts,
					goalsText: payload.goalsText || "",
				},
			},
			{ merge: true }
		);
		setOpen(false);
	};

	const handleLater = () => {
		// Suppress until the next login (sessionStorage cleared next session)
		sessionStorage.setItem(LATER_FLAG_KEY, "1");
		setOpen(false);
	};

	if (checking) return null;

	return (
		<OnboardingModal
			isOpen={open}
			onClose={handleLater}
			onComplete={handleComplete}
		/>
	);
};

export default OnboardingGate;
